import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

(function () {
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function getBoolAttr(el, name, defaultValue = false) {
    const v = el.getAttribute(name);
    if (v === null || v === '') return defaultValue;
    return v === '1' || v === 'true';
  }

  function getNumAttr(el, name, defaultValue = 0) {
    const v = el.getAttribute(name);
    if (v === null || v === '') return defaultValue;
    const n = Number(v);
    return Number.isFinite(n) ? n : defaultValue;
  }

  function parseVec3Csv(v) {
    if (!v) return null;
    const parts = String(v)
      .split(',')
      .map((s) => Number(String(s).trim()));

    if (parts.length !== 3) return null;
    if (parts.some((n) => !Number.isFinite(n))) return null;

    return { x: parts[0], y: parts[1], z: parts[2] };
  }

  function formatVec3Csv(v3) {
    const fix = (n) => {
      const x = Number(n);
      if (!Number.isFinite(x)) return '0';
      const s = x.toFixed(4);
      return s.replace(/\.?0+$/, '');
    };
    return `${fix(v3.x)},${fix(v3.y)},${fix(v3.z)}`;
  }

  async function copyToClipboard(text) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // ignore
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }

  function bindFullscreen(viewportEl, renderer, camera, controls) {
    const btn = viewportEl.querySelector('[data-bit3js-fs-btn]');

    const resize = () => {
      const w = Math.max(1, viewportEl.clientWidth);
      const h = Math.max(1, viewportEl.clientHeight);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
      controls.update();
    };

    btn?.addEventListener('click', async () => {
      try {
        if (!document.fullscreenElement) {
          await viewportEl.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch {
        // ignore
      }
    });

    document.addEventListener(
      'fullscreenchange',
      () => {
        if (document.fullscreenElement === viewportEl || !document.fullscreenElement) {
          resize();
        }
      },
      { passive: true }
    );

    return { resize };
  }

  function ensurePreloader(viewerEl) {
    let el = viewerEl.querySelector(':scope > .bit3js-preloader');
    if (!el) {
      el = document.createElement('div');
      el.className = 'bit3js-preloader';
      el.style.cssText =
        'position:absolute;inset:0;z-index:5;display:flex;align-items:center;justify-content:center;' +
        'background:rgba(0,0,0,.55);backdrop-filter:blur(2px);' +
        'opacity:0;visibility:hidden;transition:opacity .25s ease,visibility .25s ease;';

      el.innerHTML = `
        <div class="bit3js-preloader-inner" style="width:min(360px,75%);color:#fff;font:14px/1.2 system-ui,Segoe UI,Roboto,Arial;">
          <div class="bit3js-preloader-title" style="font-weight:600;margin:0 0 10px 0;">3D-Ansicht wird geladen</div>
          <div class="bit3js-preloader-bar" style="height:8px;background:rgba(255,255,255,.2);border-radius:999px;overflow:hidden;">
            <i style="display:block;height:100%;width:0%;background:rgba(255,255,255,.9);border-radius:999px;transition:width .12s linear;"></i>
          </div>
          <div class="bit3js-preloader-meta" style="margin-top:8px;opacity:.9;display:flex;justify-content:space-between;gap:12px;">
            <span data-bit3js-progress-text>0%</span>
            <span data-bit3js-progress-detail></span>
          </div>
        </div>
      `;
      viewerEl.appendChild(el);
    }

    const bar = el.querySelector('.bit3js-preloader-bar > i');
    const pctEl = el.querySelector('[data-bit3js-progress-text]');
    const detailEl = el.querySelector('[data-bit3js-progress-detail]');

    return {
      show() {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
      },
      hide() {
        el.style.opacity = '0';
        el.style.visibility = 'hidden';
      },
      setProgress(pct, detail = '') {
        const p = Math.max(0, Math.min(100, pct || 0));
        if (bar) bar.style.width = `${p}%`;
        if (pctEl) pctEl.textContent = `${Math.round(p)}%`;
        if (detailEl) detailEl.textContent = detail || '';
      }
    };
  }

  function createLoadingManager(preloader) {
    const manager = new THREE.LoadingManager();
    manager.onStart = () => preloader.setProgress(0, 'start');
    manager.onProgress = (url, loaded, total) => {
      const pct = total ? (loaded / total) * 100 : 0;
      const name = typeof url === 'string' ? url.split('/').pop() : '';
      preloader.setProgress(pct, name);
    };
    manager.onLoad = () => preloader.setProgress(100, 'done');
    manager.onError = () => preloader.setProgress(0, 'error');
    return manager;
  }

  function addDefaultMaterialIfMissing(obj) {
    const fallbackMat = new THREE.MeshStandardMaterial({
      color: 0xbfbfbf,
      roughness: 0.6,
      metalness: 0.05
    });

    obj.traverse((child) => {
      if (child && child.isMesh) {
        if (!child.material) child.material = fallbackMat;
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }

  function normalizeAndFit(group, camera, controls) {
    const box = new THREE.Box3().setFromObject(group);
    const sizeV = box.getSize(new THREE.Vector3());
    const centerV = box.getCenter(new THREE.Vector3());

    group.position.sub(centerV);

    const maxDim = Math.max(sizeV.x, sizeV.y, sizeV.z);
    if (isFinite(maxDim) && maxDim > 0) {
      const target = 400;
      let scale = target / maxDim;
      scale = Math.min(1000, Math.max(1e-6, scale));
      group.scale.setScalar(scale);
    }

    const box2 = new THREE.Box3().setFromObject(group);
    const size2 = box2.getSize(new THREE.Vector3());
    const center2 = box2.getCenter(new THREE.Vector3());

    group.position.sub(center2);

    const maxDim2 = Math.max(size2.x, size2.y, size2.z);
    const fov = (camera.fov * Math.PI) / 180;
    let dist = Math.abs((maxDim2 / 2) / Math.tan(fov / 2));
    dist *= 1.25;

    camera.near = Math.max(0.1, dist / 1000);
    camera.far = dist * 2000;
    camera.updateProjectionMatrix();

    camera.position.set(dist * 0.9, dist * 0.6, dist);
    controls.target.set(0, 0, 0);
    controls.update();
  }

  function applyInitialCameraOverrides(viewerEl, camera, controls) {
    const posOverride = parseVec3Csv(viewerEl.getAttribute('data-bit3js-cam-pos'));
    if (posOverride) {
      camera.position.set(posOverride.x, posOverride.y, posOverride.z);
    }

    const distanceFactor = getNumAttr(viewerEl, 'data-bit3js-cam-distance', 1.0);
    const factor = Number.isFinite(distanceFactor) ? distanceFactor : 1.0;

    if (factor !== 1.0) {
      const dir = new THREE.Vector3().subVectors(camera.position, controls.target);
      const base = dir.length();
      if (base > 0) {
        const safeFactor = Math.max(0.2, factor);
        dir.normalize().multiplyScalar(base * safeFactor);
        camera.position.copy(controls.target.clone().add(dir));
      }
    }

    controls.update();
  }

  function degToRad(deg) {
    const n = Number(deg);
    if (!Number.isFinite(n)) return 0;
    return (n * Math.PI) / 180;
  }

  function applyBgRotationY(scene, rad) {
    if (scene && 'backgroundRotation' in scene && scene.backgroundRotation) {
      scene.backgroundRotation.y = rad;
    }
    if (scene && 'environmentRotation' in scene && scene.environmentRotation) {
      scene.environmentRotation.y = rad;
    }
  }

  function createHdrState() {
    return {
      hdrTexture: null,
      envMap: null
    };
  }

  function clearHdr(scene, hdrState) {
    if (hdrState.envMap) {
      hdrState.envMap.dispose?.();
      hdrState.envMap = null;
    }
    if (hdrState.hdrTexture) {
      hdrState.hdrTexture.dispose?.();
      hdrState.hdrTexture = null;
    }
    scene.environment = null;
    scene.background = null;
  }

  async function applyHdrBackground(
    scene,
    renderer,
    manager,
    preloader,
    hdrUrl,
    hdrState,
    bgRotYRad = 0
  ) {
    if (!hdrUrl) return;

    clearHdr(scene, hdrState);
    preloader.setProgress(0, 'HDR loading…');

    const hdrLoader = new RGBELoader(manager);
    const hdrTexture = await new Promise((resolve, reject) => {
      hdrLoader.load(
        String(hdrUrl),
        (tex) => resolve(tex),
        (xhr) => {
          if (xhr && xhr.total) {
            const pct = (xhr.loaded / xhr.total) * 100;
            preloader.setProgress(pct, `HDR ${Math.round(pct)}%`);
          }
        },
        (err) => reject(err)
      );
    });

    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    hdrTexture.mapping = THREE.EquirectangularReflectionMapping;
    scene.background = hdrTexture;

    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const envMap = pmrem.fromEquirectangular(hdrTexture).texture;
    pmrem.dispose();

    scene.environment = envMap;

    if ('backgroundIntensity' in scene) scene.backgroundIntensity = 1.0;
    if ('environmentIntensity' in scene) scene.environmentIntensity = 1.0;

    applyBgRotationY(scene, bgRotYRad);

    hdrState.hdrTexture = hdrTexture;
    hdrState.envMap = envMap;

    preloader.setProgress(100, 'HDR ready');
  }

  async function initGuiIfEnabled(viewerEl, scene, renderer, camera, controls, helpers) {
    const enableGui = getBoolAttr(viewerEl, 'data-bit3js-gui', false);
    if (!enableGui) return;

    let GUI;
    try {
      ({ GUI } = await import('lil-gui'));
    } catch {
      console.warn('bit3js: GUI requested but lil-gui not available in build.');
      return;
    }

    const host = document.createElement('div');
    host.className = 'bit3js-gui-host';
    host.style.cssText =
      'position:absolute;top:10px;right:10px;z-index:6;max-width:min(320px,90%);';
    viewerEl.appendChild(host);

    const gui = new GUI({
      container: host,
      title: 'bit3js Helper'
    });

    const fHelper = gui.addFolder('Helper');
    const helperState = {
      axes: !!helpers.axes,
      grid: !!helpers.grid
    };

    fHelper.add(helperState, 'axes').name('Axes').onChange((v) => {
      if (v) {
        if (!helpers.axes) {
          helpers.axes = new THREE.AxesHelper(200);
        }
        scene.add(helpers.axes);
      } else if (helpers.axes) {
        scene.remove(helpers.axes);
      }
    });

    fHelper.add(helperState, 'grid').name('Grid').onChange((v) => {
      if (v) {
        if (!helpers.grid) {
          const g = new THREE.GridHelper(2000, 40);
          g.material.opacity = 0.35;
          g.material.transparent = true;
          helpers.grid = g;
        }
        scene.add(helpers.grid);
      } else if (helpers.grid) {
        scene.remove(helpers.grid);
      }
    });

    const fCam = gui.addFolder('Camera');
    const camState = {
      camX: camera.position.x,
      camY: camera.position.y,
      camZ: camera.position.z,
      tgtX: controls.target.x,
      tgtY: controls.target.y,
      tgtZ: controls.target.z,
      exposure: renderer.toneMappingExposure ?? 1.0,
      copyCamPos: async () => {
        const txt = formatVec3Csv(camera.position);
        await copyToClipboard(txt);
        console.log('bit3js cam-pos:', txt);
      },
      copyTarget: async () => {
        const txt = formatVec3Csv(controls.target);
        await copyToClipboard(txt);
        console.log('bit3js target:', txt);
      },
      resetView: () => {
        controls.target.set(0, 0, 0);
        camera.position.set(800, 500, 900);
        controls.update();
      }
    };

    const syncFromThree = () => {
      camState.camX = camera.position.x;
      camState.camY = camera.position.y;
      camState.camZ = camera.position.z;
      camState.tgtX = controls.target.x;
      camState.tgtY = controls.target.y;
      camState.tgtZ = controls.target.z;
    };

    const applyCamPos = () => {
      camera.position.set(camState.camX, camState.camY, camState.camZ);
      controls.update();
    };

    const applyTarget = () => {
      controls.target.set(camState.tgtX, camState.tgtY, camState.tgtZ);
      controls.update();
    };

    fCam.add(camState, 'camX').name('pos.x').listen().onChange(applyCamPos);
    fCam.add(camState, 'camY').name('pos.y').listen().onChange(applyCamPos);
    fCam.add(camState, 'camZ').name('pos.z').listen().onChange(applyCamPos);

    fCam.add(camState, 'tgtX').name('target.x').listen().onChange(applyTarget);
    fCam.add(camState, 'tgtY').name('target.y').listen().onChange(applyTarget);
    fCam.add(camState, 'tgtZ').name('target.z').listen().onChange(applyTarget);

    fCam.add(camState, 'exposure', 0.1, 3.0, 0.01).name('exposure').onChange((v) => {
      renderer.toneMappingExposure = Number(v) || 1.0;
    });

    fCam.add(camState, 'copyCamPos').name('Copy cam-pos');
    fCam.add(camState, 'copyTarget').name('Copy target');
    fCam.add(camState, 'resetView').name('Reset view');

    const onEnd = () => syncFromThree();
    controls.addEventListener('end', onEnd);

    viewerEl.addEventListener(
      'bit3js:dispose',
      () => {
        try {
          controls.removeEventListener('end', onEnd);
          gui.destroy();
          host.remove();
        } catch {
          // ignore
        }
      },
      { once: true }
    );
  }

  function initAll() {
    const viewers = document.querySelectorAll('[data-bit3js], [data-bit3js-preview]');
    if (!viewers.length) return false;

    viewers.forEach((viewerEl) => {
      if (viewerEl.dataset.bit3jsInitialized === '1') return;
      viewerEl.dataset.bit3jsInitialized = '1';

      const isPreviewMode = viewerEl.hasAttribute('data-bit3js-preview');

      viewerEl.style.position = viewerEl.style.position || 'relative';
      viewerEl.style.overflow = 'hidden';

      const preloader = ensurePreloader(viewerEl);
      preloader.setProgress(0, '');

      const wrapper = viewerEl.closest('[data-bit3js-wrapper]');
      let jsonEl =
        wrapper?.querySelector('script[data-bit3js-models]') ||
        viewerEl.parentElement?.querySelector('script[data-bit3js-models]') ||
        null;

      if (!jsonEl) {
        const nextEl = viewerEl.nextElementSibling;
        if (nextEl && nextEl.matches('script[data-bit3js-models]')) {
          jsonEl = nextEl;
        }
      }

      if (!jsonEl) {
        preloader.hide();
        return;
      }

      let models = [];
      try {
        models = JSON.parse((jsonEl.textContent || '').trim() || '[]');
      } catch {
        preloader.hide();
        return;
      }

      if (!Array.isArray(models) || !models.length) {
        preloader.hide();
        return;
      }

      if (!isPreviewMode) {
        preloader.show();
      }

      const width = isPreviewMode
        ? Math.max(1, getNumAttr(viewerEl, 'data-bit3js-preview-width', 800))
        : Math.max(1, viewerEl.clientWidth);

      const height = isPreviewMode
        ? Math.max(1, getNumAttr(viewerEl, 'data-bit3js-preview-height', 600))
        : Math.max(1, viewerEl.clientHeight);

      const scene = new THREE.Scene();
      scene.background = null;
      scene.environment = null;

      const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 10000000);
      camera.position.set(800, 500, 900);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(width, height, false);
      renderer.setClearColor(0x000000, 0);

      const canvas = renderer.domElement;
      canvas.style.position = 'absolute';
      canvas.style.inset = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      canvas.style.zIndex = '1';
      canvas.style.background = 'transparent';
      viewerEl.appendChild(canvas);

      scene.add(new THREE.AmbientLight(0xffffff, 0.75));
      const dir = new THREE.DirectionalLight(0xffffff, 1.2);
      dir.position.set(200, 400, 200);
      scene.add(dir);

      const wantAxes = !isPreviewMode && getBoolAttr(viewerEl, 'data-bit3js-show-axes', false);
      const wantGrid = !isPreviewMode && getBoolAttr(viewerEl, 'data-bit3js-show-grid', false);

      const helpers = { axes: null, grid: null };

      if (wantAxes) {
        helpers.axes = new THREE.AxesHelper(200);
        scene.add(helpers.axes);
      }
      if (wantGrid) {
        const g = new THREE.GridHelper(2000, 40);
        g.material.opacity = 0.35;
        g.material.transparent = true;
        helpers.grid = g;
        scene.add(helpers.grid);
      }

      const group = new THREE.Group();
      scene.add(group);

      const controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;

      if (isPreviewMode) {
        controls.enablePan = false;
        controls.enableZoom = false;
        controls.enableRotate = false;
      }

      viewerEl.__bit3jsRuntime = {
        scene,
        renderer,
        camera,
        controls,
        helpers,
        group
      };

      if (!isPreviewMode) {
        bindFullscreen(viewerEl, renderer, camera, controls);
      } else {
        window.bit3jsPreviewReady = false;
        window.bit3jsPreviewError = null;
        viewerEl.dataset.bit3jsPreviewReady = '0';
      }

      const manager = createLoadingManager(preloader);
      const stlLoader = new STLLoader(manager);
      const gltfLoader = new GLTFLoader(manager);
      const objLoader = new OBJLoader(manager);
      const mtlLoader = new MTLLoader(manager);

      const bgMode = isPreviewMode
        ? 'color'
        : String(viewerEl.getAttribute('data-bit3js-bg-mode') || 'color');

      const bgUrl = isPreviewMode
        ? ''
        : String(viewerEl.getAttribute('data-bit3js-bg-url') || '');

      const bgRotYDeg = isPreviewMode
        ? 0
        : getNumAttr(viewerEl, 'data-bit3js-bg-rot-y', 0);

      const bgRotYRad = degToRad(bgRotYDeg);

      const hdrState = createHdrState();

      let idx = 0;

      const loadNext = () => {
        const m = models[idx];

        if (!m) {
          normalizeAndFit(group, camera, controls);
          applyInitialCameraOverrides(viewerEl, camera, controls);

          if (!isPreviewMode) {
            initGuiIfEnabled(viewerEl, scene, renderer, camera, controls, helpers);
            preloader.setProgress(100, 'done');
            preloader.hide();
          } else {
            preloader.hide();
            renderer.render(scene, camera);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                viewerEl.dataset.bit3jsPreviewReady = '1';
                window.bit3jsPreviewReady = true;
                viewerEl.dispatchEvent(new CustomEvent('bit3js:preview-ready'));
                window.dispatchEvent(
                  new CustomEvent('bit3js:preview-ready', {
                    detail: {
                      viewerId: viewerEl.id || null
                    }
                  })
                );
              });
            });
          }

          return;
        }

        const url = String(m.fileUrl || m.url || '');
        const ext = String(m.fileExt || m.extension || '').toLowerCase();
        const fileMtlUrl = String(m.fileMtlUrl || '');

        if (!url || !ext) {
          idx++;
          loadNext();
          return;
        }

        if (ext === 'stl') {
          stlLoader.load(
            url,
            (geom) => {
              const mat = new THREE.MeshStandardMaterial({
                color: 0xcfcfcf,
                roughness: 0.6,
                metalness: 0.05
              });

              const mesh = new THREE.Mesh(geom, mat);
              mesh.castShadow = true;
              mesh.receiveShadow = true;
              mesh.rotation.x = -Math.PI / 2;

              group.add(mesh);
              idx++;
              loadNext();
            },
            undefined,
            (error) => {
              console.error('bit3js STL load error', error);
              idx++;
              loadNext();
            }
          );
          return;
        }

        if (ext === 'glb' || ext === 'gltf') {
          gltfLoader.load(
            url,
            (gltf) => {
              const obj = gltf.scene;
              obj.traverse((n) => {
                if (n.isMesh) {
                  n.castShadow = true;
                  n.receiveShadow = true;
                }
              });
              group.add(obj);
              idx++;
              loadNext();
            },
            undefined,
            (error) => {
              console.error('bit3js GLTF load error', error);
              idx++;
              loadNext();
            }
          );
          return;
        }

        if (ext === 'obj') {
          const mtlUrl = fileMtlUrl || url.replace(/\.obj(\?|#|$)/i, '.mtl$1');

          mtlLoader.load(
            mtlUrl,
            (materials) => {
              materials.preload();
              objLoader.setMaterials(materials);
              objLoader.load(
                url,
                (obj) => {
                  addDefaultMaterialIfMissing(obj);
                  group.add(obj);
                  idx++;
                  loadNext();
                },
                undefined,
                (error) => {
                  console.error('bit3js OBJ load error with MTL', error);
                  idx++;
                  loadNext();
                }
              );
            },
            undefined,
            () => {
              objLoader.load(
                url,
                (obj) => {
                  addDefaultMaterialIfMissing(obj);
                  group.add(obj);
                  idx++;
                  loadNext();
                },
                undefined,
                (error) => {
                  console.error('bit3js OBJ load error fallback', error);
                  idx++;
                  loadNext();
                }
              );
            }
          );
          return;
        }

        idx++;
        loadNext();
      };

      (async function boot() {
        try {
          if (bgMode === 'none') {
            clearHdr(scene, hdrState);
            scene.background = null;
            scene.environment = null;
          } else if (bgMode === 'hdr360' && bgUrl) {
            try {
              await applyHdrBackground(scene, renderer, manager, preloader, bgUrl, hdrState, bgRotYRad);
            } catch {
              clearHdr(scene, hdrState);
              scene.background = null;
              scene.environment = null;
            }
          } else {
            clearHdr(scene, hdrState);
            scene.background = null;
            scene.environment = null;
          }

          applyBgRotationY(scene, bgRotYRad);
          loadNext();
        } catch (e) {
          console.error('bit3js boot error', e);
          if (isPreviewMode) {
            window.bit3jsPreviewReady = false;
            window.bit3jsPreviewError = e instanceof Error ? e.message : String(e);
            viewerEl.dataset.bit3jsPreviewReady = '0';
          }
          preloader.hide();
        }
      })();

      (function animate() {
        controls.update();
        renderer.render(scene, camera);
        requestAnimationFrame(animate);
      })();

      if (!isPreviewMode) {
        window.addEventListener(
          'resize',
          () => {
            const w = Math.max(1, viewerEl.clientWidth);
            const h = Math.max(1, viewerEl.clientHeight);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h, false);
            controls.update();
          },
          { passive: true }
        );
      }

      viewerEl.addEventListener(
        'bit3js:dispose',
        () => {
          try {
            delete viewerEl.__bit3jsRuntime;
          } catch {
            // ignore
          }
        },
        { once: true }
      );
    });

    return true;
  }

  onReady(() => {
    if (initAll()) return;
    let tries = 0;
    const t = setInterval(() => {
      tries++;
      if (initAll() || tries >= 10) clearInterval(t);
    }, 200);
  });
})();